/**
 * TankCard.jsx  v4
 * Header: nombre + "Propano · 548.250 lts"
 * Bajo la masa: "45% · densidad 0.560 kg/ltr"
 * Sin "de XXXX" redundante al final
 */

import {
    Box, Card, CardContent, Chip, Divider,
    Stack, Typography, alpha, useTheme,
} from '@mui/material';
import { ThermostatOutlined, SpeedOutlined } from '@mui/icons-material';
import LiquidGauge from './Liquidgauge';
import { getTankStatus, statusColor, statusLabel, calcMasa, calcDensidad } from './Hipergas.utils';

export default function TankCard({ tank, current, cfg, selected, onSelect }) {
    const theme = useTheme();
    const nivel = current[`TK_${tank.id}`] || 0;
    const temp = current[`TEMP_${tank.id}`] || 0;
    const presion = current[`PRESION_${tank.id}`] || 0;
    const status = getTankStatus(tank, current, cfg);
    const sc = statusColor(status);
    const sl = statusLabel(status);
    const sel = selected === tank.id;
    const sinSenal = nivel === 0; // sensor en 0 — probable pérdida de señal

    const masa = calcMasa(cfg, nivel, temp);
    const densidad = calcDensidad(cfg, temp).toFixed(3);
    const producto = cfg.producto || 'propano';
    const capLts = (cfg.capacidad_lts || 0).toLocaleString('es-AR');

    const tempAlert = temp > (cfg.temp_max || 35) || temp < (cfg.temp_min || 0);
    const presAlert = presion > (cfg.presion_max || 4.5) || presion < (cfg.presion_min || 0);

    return (
        <Card
            onClick={() => onSelect(tank.id)}
            elevation={0}
            sx={{
                cursor: 'pointer',
                border: `1px solid ${sel ? (sinSenal ? '#9CA3AF' : sc) : alpha(theme.palette.divider, 0.5)}`,
                transition: 'all 0.18s ease',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                '&:hover': {
                    borderColor: sinSenal ? '#9CA3AF' : sc,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 20px ${alpha(sinSenal ? '#9CA3AF' : sc, 0.15)}`,
                },
            }}
        >
            {/* Accent top */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: sinSenal ? '#9CA3AF' : sc, transition: 'background 0.4s ease' }} />

            <CardContent sx={{ p: '14px 16px !important' }}>

                {/* Header: nombre + badge estado */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5, color: 'text.primary', textTransform: 'uppercase' }}>
                            {cfg.label || tank.label}
                        </Typography>
                        {/* Producto + capacidad total — una línea limpia */}
                        <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 10, lineHeight: 1.3, textTransform: 'capitalize' }}>
                            {producto} · {capLts} lts
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={sinSenal ? 'SIN SEÑAL' : sl}
                        sx={{
                            height: 22, fontSize: 10, fontWeight: 700,
                            bgcolor: alpha(sinSenal ? '#9CA3AF' : sc, 0.12),
                            color: sinSenal ? '#6B7280' : sc,
                            border: `1px solid ${alpha(sinSenal ? '#9CA3AF' : sc, 0.35)}`,
                            '& .MuiChip-label': { px: 1 },
                        }}
                    />
                </Stack>

                {/* Cuerpo: gauge | masa disponible | divider | temp + presión */}
                <Stack direction="row" alignItems="center" spacing={2}>

                    {/* Gauge líquido animado */}
                    <Box sx={{ flexShrink: 0 }}>
                        <LiquidGauge pct={nivel} color={sinSenal ? '#9CA3AF' : sc} size={110} />
                    </Box>

                    {/* Masa disponible */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, display: 'block', mb: 0.3 }}>
                            disponibles
                        </Typography>
                        {sinSenal ? (
                            <>
                                <Typography sx={{
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: 22, fontWeight: 700,
                                    color: '#9CA3AF',
                                    lineHeight: 1.2, letterSpacing: -0.5,
                                }}>
                                    — —
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.4 }}>
                                    último dato no disponible
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography sx={{
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: 35, fontWeight: 700,
                                    color: alpha(sc, 0.9),
                                    lineHeight: 1, letterSpacing: -0.5,
                                }}>
                                    {masa.valor}
                                </Typography>
                                <Typography variant="caption" sx={{ color: sc, fontSize: 11, fontWeight: 600 }}>
                                    {masa.unidad}
                                </Typography>
                                {/* Porcentaje + densidad — reemplaza el "de XXXX" redundante */}
                                <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.4 }}>
                                    {nivel}% · densidad {densidad} kg/ltr
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Divider vertical */}
                    <Divider orientation="vertical" flexItem sx={{ opacity: 0.25 }} />

                    {/* Temperatura + Presión */}
                    <Stack spacing={1.5} sx={{ minWidth: 90, flexShrink: 0 }}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.4} mb={0.3}>
                                <ThermostatOutlined sx={{ fontSize: 11, color: tempAlert ? 'warning.main' : 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Temperatura
                                </Typography>
                            </Stack>
                            <Typography sx={{
                                fontFamily: "'Orbitron', sans-serif", fontSize: 20, fontWeight: 700,
                                color: tempAlert ? 'warning.main' : 'text.primary', lineHeight: 1,
                            }}>
                                {temp.toFixed(1)}
                                <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontFamily: 'inherit' }}>°C</Typography>
                            </Typography>
                        </Box>

                        <Divider sx={{ opacity: 0.2 }} />

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.4} mb={0.3}>
                                <SpeedOutlined sx={{ fontSize: 11, color: presAlert ? 'error.main' : 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Presión
                                </Typography>
                            </Stack>
                            <Typography sx={{
                                fontFamily: "'Orbitron', sans-serif", fontSize: 20, fontWeight: 700,
                                color: presAlert ? 'error.main' : 'text.primary', lineHeight: 1,
                            }}>
                                {presion.toFixed(2)}
                                <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontFamily: 'inherit' }}> bar</Typography>
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}